import moment from 'moment';
import React from 'react';
import useSWR from 'swr';

import {
  ButtonBase,
  Tooltip,
} from '@material-ui/core';
import VisibilityRoundedIcon from '@material-ui/icons/VisibilityRounded';
import { Skeleton } from '@material-ui/lab';

import Button from 'creative-components/CustomButtons/Button';

import { getTouchDisplayName } from 'utils/lib';
import {
  type TouchPreviewData,
  loadTouchPreviewData,
} from 'utils/touch-preview';

const ThumbnailPreview = ({
  classes,
  previewData,
  title,
}: {
  classes: any;
  previewData: TouchPreviewData;
  title: string;
}) => {
  const viewportRef = React.useRef<HTMLDivElement | null>(null);
  const [viewportSize, setViewportSize] = React.useState({ width: 0, height: 0 });

  React.useEffect(() => {
    const element = viewportRef.current;
    if (!element) return undefined;

    const updateSize = () => {
      const nextSize = {
        width: element.clientWidth,
        height: element.clientHeight,
      };

      setViewportSize((currentSize) => (
        currentSize.width === nextSize.width && currentSize.height === nextSize.height
          ? currentSize
          : nextSize
      ));
    };

    updateSize();
    window.addEventListener('resize', updateSize);

    return () => {
      window.removeEventListener('resize', updateSize);
    };
  }, []);

  const scale = viewportSize.width > 0 && viewportSize.height > 0
    ? Math.min(
      viewportSize.width / previewData.thumbnailWidth,
      viewportSize.height / previewData.thumbnailHeight,
    )
    : 1;

  return (
    <div ref={viewportRef} className={classes.touchCardThumbnailViewport}>
      <iframe
        src={previewData.thumbnailUrl}
        title={title}
        className={`${classes.touchCardThumbnailFrame} ${classes.touchCardThumbnailCanvas}`}
        style={{
          width: `${previewData.thumbnailWidth}px`,
          height: `${previewData.thumbnailHeight}px`,
          transform: `translate(-50%, -50%) scale(${scale})`,
        }}
      />
    </div>
  );
};

interface ReuseTouchOptionCardProps {
  candidateTouchTrigger: any;
  classes: any;
  isSelected: boolean;
  shouldPreload: boolean;
  onOpenPreview: (touchTrigger: any, previewData: TouchPreviewData | undefined) => void;
  onSelect: (touchTriggerId: string) => void;
}

const ReuseTouchOptionCard = ({
  candidateTouchTrigger,
  classes,
  isSelected,
  shouldPreload,
  onOpenPreview,
  onSelect,
}: ReuseTouchOptionCardProps) => {
  const { data: previewData, error } = useSWR(
    shouldPreload ? ['touch-preview', candidateTouchTrigger._id] : null,
    async () => loadTouchPreviewData(candidateTouchTrigger),
    {
      revalidateOnFocus: false,
      shouldRetryOnError: false,
    },
  );

  const canPreview = !candidateTouchTrigger.requiresCustomTouchTemplate || !!candidateTouchTrigger.customTouchTemplate;

  const renderPreviewThumbnail = () => {
    if (!canPreview) {
      return (
        <div className={classes.touchCardThumbnailError}>
          Customize this touch to unlock its preview.
        </div>
      );
    }

    if (error) {
      return (
        <div className={classes.touchCardThumbnailError}>
          Preview unavailable right now.
        </div>
      );
    }

    if (!previewData) {
      return (
        <div className={classes.touchCardThumbnailLoading}>
          <Skeleton variant="rect" width="100%" height={110} />
          <Skeleton variant="text" width="100%" />
          <Skeleton variant="text" width="82%" />
        </div>
      );
    }

    return (
      <ThumbnailPreview
        classes={classes}
        previewData={previewData}
        title={`${getTouchDisplayName(candidateTouchTrigger)} thumbnail`}
      />
    );
  };

  return (
    <div
      className={[
        classes.touchCard,
        isSelected ? classes.touchCardSelected : '',
      ].join(' ')}
    >
      <div>
        <div className={classes.touchCardTitle}>
          {getTouchDisplayName(candidateTouchTrigger)}
          {' - '}
          <span className={classes.touchCardTitleDate}>
            {moment(candidateTouchTrigger.sendAt).format('MMMM Do, YYYY')}
          </span>
        </div>
        <ButtonBase
          className={classes.touchCardThumbnail}
          disabled={!canPreview}
          onClick={() => onOpenPreview(candidateTouchTrigger, previewData)}
        >
          {renderPreviewThumbnail()}
        </ButtonBase>
      </div>

      <div className={classes.touchCardActions}>
        <Tooltip title="Select this touch" placement="top">
          <span>
            <ButtonBase
              className={`${classes.touchCardSelectButton} ${isSelected ? classes.touchCardSelectButtonSelected : ''}`}
              onClick={() => onSelect(candidateTouchTrigger._id)}
            >
              {isSelected ? 'Selected' : 'Select'}
            </ButtonBase>
          </span>
        </Tooltip>

        <Tooltip title={canPreview ? 'Preview this touch' : 'Customize this touch before previewing it'} placement="top">
          <span>
            <Button
              color="dark"
              simple
              className={classes.touchCardPreviewButton}
              disabled={!canPreview}
              onClick={() => onOpenPreview(candidateTouchTrigger, previewData)}
            >
              <VisibilityRoundedIcon />
              Preview
            </Button>
          </span>
        </Tooltip>
      </div>
    </div>
  );
};

export default ReuseTouchOptionCard;
